/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useReducer } from "react";
import { useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { useLocalstorageState } from "rooks";

import { getUser } from "@/api";
import { useWebSocket } from "@/hooks/useWebSocket";
import { User } from "@/types";

import { LOGIN, LOGOUT, UPDATE_USER_INFO } from "./../types";
import AuthReducer from "./AuthReducer";
import { AuthContext, authInitialState } from "./context";

declare const window: any;

const STORAGE_KEY = "sec3-user-state";

// Keys that hold per-account state and must be cleared on sign-out /
// account switch, otherwise the previously signed-in user's data leaks
// into the new session (e.g. the Monitor/WatchTower tab rendering the
// previous account's bots with a mismatched "Total: 0" footer).
const SESSION_LOCAL_STORAGE_KEYS = [
  "sec3-monitor",
  "sec3-current-project",
  "sec3-data-state",
  "update-bot-state",
];

const SESSION_SESSION_STORAGE_KEYS = ["sec3-data-state", "bot-templates"];

const clearSessionStorageKeys = () => {
  try {
    SESSION_LOCAL_STORAGE_KEYS.forEach((key) => {
      window.localStorage.removeItem(key);
    });
    SESSION_SESSION_STORAGE_KEYS.forEach((key) => {
      window.sessionStorage.removeItem(key);
    });
  } catch {
    // storage may be unavailable in some environments; ignore.
  }
};

interface Props {
  children: React.ReactNode;
}

export const AuthProvider = ({ children }: Props) => {
  const [value, set] = useLocalstorageState(STORAGE_KEY, authInitialState);
  const [state, dispatch] = useReducer(AuthReducer, value);
  const { connectWebSocket, disconnectWebSocket } = useWebSocket();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    set(state);
    return () => {};
  }, [state, set]);

  useEffect(() => {
    // Always refresh when context is initialized
    refreshUserInfo();
  }, []);

  const login = async (payload: User) => {
    // Clear any residual cache from a previous account before starting a
    // new session. This prevents data leakage when a user logs directly
    // into a different account without signing out first.
    queryClient.clear();
    dispatch({ type: LOGIN, payload });
    connectWebSocket();
  };

  const githubLogin = async (code: string) => {
    queryClient.clear();
    dispatch({ type: LOGIN, payload: { code } });
    connectWebSocket();
  };

  const updateUserInfo = async (payload: User) => {
    dispatch({ type: UPDATE_USER_INFO, payload });
  };

  const logout = () => {
    window.localStorage.removeItem("jwt");
    clearSessionStorageKeys();
    // Drop every cached query so that the next signed-in user cannot see
    // the previous user's projects, bots, alerts, etc. Downstream context
    // providers (Monitor, Data) reset their own in-memory state by
    // observing the isLoggedIn -> false transition.
    queryClient.clear();
    dispatch({ type: LOGOUT });
    disconnectWebSocket();
    navigate("/");
  };

  const refreshUserInfo = async () => {
    // Only when the token exists does the client fetch user
    if (localStorage.getItem("jwt")) {
      const user = await getUser();
      await updateUserInfo(user);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        githubLogin,
        updateUserInfo,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
